import {TOQUES_PADRAO_CARRINHO, TOQUES_PADRAO_DESCOBERTA} from "./ToquesDeFollowupMiaV3";

/**
 * Catálogo dos campos de configuração da Mia: o que cada ajuste é, em português,
 * fora do HTML da tela.
 *
 * Fonte compartilhada de rótulos, ajuda, padrões, grupos, permissões e
 * dependências para as telas de configuração e seus resumos.
 *
 * Regra da casa: explicação não se corta, se reorganiza. `explicacao` é o texto
 * de ajuda completo; `porQueMudar` é a linha nova, com o caso concreto.
 *
 * `momento` define o grupo em todas as telas. Permissões e dependências
 * também são compartilhadas; os componentes cuidam da apresentação.
 */

export type TipoCampoMia =
  | 'escolha'    // uma opção entre poucas (rádio/chips)
  | 'switch'     // liga/desliga
  | 'numero'     // inteiro com unidade
  | 'texto'      // linha ou parágrafo
  | 'telefones'  // lista de números separada por vírgula
  | 'produtos'   // até N produtos do cardápio, em ordem
  | 'escada';    // até 3 tempos crescentes em minutos (JSON)

/** Etapas do pedido e assuntos que atravessam toda a conversa. */
export type MomentoDaConversa = 'operacao' | 'chegada' | 'conversa' | 'descoberta'
  | 'montagem' | 'fechamento' | 'equipe' | 'depois' | 'legado' | 'motor';

/**
 * Os grupos vivem em dois blocos: os PASSOS seguem a ordem do pedido (a Mia faz
 * X) e os assuntos de SEMPRE valem em qualquer ponto da conversa. As telas
 * mostram os dois com rótulo próprio, para o lojista não procurar "áudio" numa
 * etapa do pedido.
 */
export type BlocoDeGrupos = 'passo' | 'sempre';

export const BLOCOS_CONFIG_MIA: Array<{ chave: BlocoDeGrupos; rotulo: string }> = [
  {chave: 'passo', rotulo: 'Passo a passo'},
  {chave: 'sempre', rotulo: 'O tempo todo'},
];

export const GRUPOS_CONFIG_MIA: Array<{
  chave: MomentoDaConversa; bloco: BlocoDeGrupos; rotulo: string; icone: string; desc: string; soAdmin?: boolean;
}> = [
  {chave: 'chegada', bloco: 'passo', rotulo: 'Recebe o cliente', icone: 'fa-door-open',
    desc: 'Nome, saudação e reconhecimento de quem já pediu.'},
  {chave: 'descoberta', bloco: 'passo', rotulo: 'Manda o cardápio', icone: 'fa-utensils',
    desc: 'Quando manda o link, se vai por link ou foto, e os textos do botão.'},
  {chave: 'montagem', bloco: 'passo', rotulo: 'Monta o pedido', icone: 'fa-sliders-h',
    desc: 'Como o cliente escolhe os adicionais de um produto e a oferta de um item a mais.'},
  {chave: 'fechamento', bloco: 'passo', rotulo: 'Fecha o pedido', icone: 'fa-flag-checkered',
    desc: 'Endereço e dados habituais de entrega e pagamento.'},
  {chave: 'depois', bloco: 'passo', rotulo: 'Chama de volta', icone: 'fa-bell',
    desc: 'Lembretes quando o cliente some no meio da conversa ou do pedido.'},
  {chave: 'conversa', bloco: 'sempre', rotulo: 'Texto, áudio e foto', icone: 'fa-comment-dots',
    desc: 'Como a Mia escreve, o que faz com áudios e imagens, e instruções específicas da loja.'},
  {chave: 'equipe', bloco: 'sempre', rotulo: 'Atendimento humano', icone: 'fa-headset',
    desc: 'Transferência para uma pessoa, aviso ao cliente e retorno da Mia.'},
  {chave: 'legado', bloco: 'sempre', rotulo: 'Bot antigo', icone: 'fa-history',
    desc: 'Ajustes do caminho antigo e do carrinho digital. Não alteram o atendimento da Mia V3.'},
  {chave: 'motor', bloco: 'sempre', rotulo: 'Motor', icone: 'fa-cogs', soAdmin: true,
    desc: 'Ativação da Mia V3, carrinho conversacional, modelo e diagnóstico do serviço.'},
];

/**
 * O que muda quando o ajuste muda:
 * - resposta: o que a Mia diz num turno — o simulador consegue mostrar sem salvar;
 * - operacao: quem recebe resposta e quando a Mia se cala;
 * - agenda: mensagens proativas enviadas depois, fora do turno;
 * - motor: qual motor atende e com que modelo.
 */
export type EfeitoDoCampo = 'resposta' | 'operacao' | 'agenda' | 'motor';

export interface OpcaoCampoMia {
  valor: string;
  icone: string;
  nome: string;
  descricao: string;
  /** Frase final em destaque, quando a opção tem uma consequência a avisar. */
  reforco?: string;
  /** Versão curta, para resumos de cabeçalho e diffs. */
  resumo: string;
}

export interface DependenciaDeCampo {
  chave: string;
  /** O campo só faz sentido quando `chave` vale isto (omitido = quando é verdadeiro). */
  valor?: any;
  /** ...ou quando `chave` é diferente disto. */
  diferenteDe?: any;
}

export interface CampoConfigMia {
  /** Nome da propriedade em ConfiguracoesMia e no objeto `configuracoes` da tela. */
  chave: string;
  rotulo: string;
  tipo: TipoCampoMia;
  /** Valor inicial; null numa escolha = nasce sem opção marcada. */
  padrao: any;
  icone: string;
  momento: MomentoDaConversa;
  efeito: EfeitoDoCampo;
  quemEdita: 'lojista' | 'admin';
  /** Uma linha: em que situação o lojista mudaria isto. */
  porQueMudar: string;
  /** Texto de ajuda completo da tela. */
  explicacao: string;
  /** Como o lojista fala disto — alimenta busca e copiloto. */
  sinonimos: string[];
  /**
   * A frase do resumo ("É assim que sua Mia atende") para o valor atual — verbo
   * na frente, sujeito implícito. É o que a tela nova mostra em vez do controle.
   */
  frase: (valor: any, config?: any) => string;
  opcoes?: OpcaoCampoMia[];
  unidade?: 'segundos' | 'minutos' | 'caracteres' | 'itens';
  min?: number;
  max?: number;
  maxLength?: number;
  /** Só aparece/faz sentido quando outro campo tem certo valor. */
  dependeDe?: DependenciaDeCampo;
  /** Salvo, mas sem controle próprio na tela (derivado ou fora da navegação). */
  oculto?: boolean;
  /** Controle especializado disponível na tela avançada. */
  somenteAvancado?: boolean;
  /** Personalização secundária dentro do grupo. */
  detalhe?: boolean;
  /** Referenciado só pela cadeia antiga: nenhum arquivo do caminho da Mia V3 lê este campo. */
  foraDaMiaV3?: boolean;
}

/** "1800" → "30 minutos"; "90" → "1 minuto e 30 segundos". */
export function formateDuracao(segundos: number): string {
  const total = Math.max(0, Math.round(Number(segundos) || 0));
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), sg = total % 60;
  const partes: string[] = [];
  if (h) partes.push(`${h} ${h === 1 ? 'hora' : 'horas'}`);
  if (m) partes.push(`${m} ${m === 1 ? 'minuto' : 'minutos'}`);
  if (sg || !partes.length) partes.push(`${sg} ${sg === 1 ? 'segundo' : 'segundos'}`);
  return partes.length > 1 ? `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}` : partes[0];
}

/** [2, 8, 25] → "2, 8 e 25". Aceita o JSON salvo no banco. */
export function formateLista(valores: any): string {
  let lista: any = valores;
  if (typeof lista === 'string') { try { lista = JSON.parse(lista || '[]'); } catch { lista = []; } }
  const itens = (Array.isArray(lista) ? lista : []).map(String);
  if (!itens.length) return '';
  return itens.length > 1 ? `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}` : itens[0];
}

function nomeDaOpcao(opcoes: OpcaoCampoMia[], valor: any): string {
  return opcoes.find((opcao) => opcao.valor === valor)?.nome || String(valor ?? '');
}

function conteItens(valor: any): number {
  let lista: any = valor;
  if (typeof lista === 'string') { try { lista = JSON.parse(lista || '[]'); } catch { lista = []; } }
  return Array.isArray(lista) ? lista.length : 0;
}

export const OPCOES_STATUS_MIA: OpcaoCampoMia[] = [
  {
    valor: 'DESATIVADA', icone: 'fa-power-off', nome: 'Desligada',
    descricao: 'Ninguém recebe respostas automáticas da Mia. Atendimento fica 100% manual.',
    resumo: 'desligada',
  },
  {
    valor: 'MODO_TESTE', icone: 'fa-flask', nome: 'Modo Teste',
    descricao: 'Apenas os números autorizados da lista de testes conversam com a Mia.',
    reforco: 'Recomendado para começar.', resumo: 'modo teste',
  },
  {
    valor: 'ATIVADA', icone: 'fa-rocket', nome: 'Em Produção',
    descricao: 'Todos os clientes do seu estabelecimento são atendidos automaticamente pela Mia.',
    resumo: 'em produção',
  },
];

export const OPCOES_FORA_DO_ESCOPO: OpcaoCampoMia[] = [
  {
    valor: 'RESPONDER', icone: 'fa-comment-dots', nome: 'Responder mesmo assim',
    descricao: 'A Mia tenta responder qualquer pergunta.',
    resumo: 'responde tudo',
  },
  {
    valor: 'NAO_RESPONDER', icone: 'fa-comment-slash', nome: 'Não responder',
    descricao: 'A Mia ignora a pergunta e deixa a mensagem como não lida.',
    resumo: 'ignora',
  },
  {
    valor: 'CHAMAR_ATENDENTE', icone: 'fa-headset', nome: 'Chamar um atendente',
    descricao: 'A Mia passa a conversa para uma pessoa da equipe.',
    resumo: 'chama atendente',
  },
];

export const OPCOES_AUDIO: OpcaoCampoMia[] = [
  {
    valor: 'TRANSCREVER', icone: 'fa-comment-dots', nome: 'Entender e responder',
    descricao: 'A Mia transcreve o áudio e responde por texto como se o cliente tivesse escrito. ' +
      'Se não conseguir entender, pede a mensagem por texto.',
    resumo: 'entende e responde',
  },
  {
    valor: 'PEDIR_TEXTO', icone: 'fa-keyboard', nome: 'Pedir para escrever',
    descricao: 'A Mia avisa que não consegue ouvir áudios e pede a mensagem por texto.',
    reforco: 'Se o cliente insistir e mandar outro áudio, a conversa é passada para um atendente.',
    resumo: 'pede para escrever',
  },
  {
    valor: 'CHAMAR_ATENDENTE', icone: 'fa-headset', nome: 'Chamar um atendente',
    descricao: 'Já no primeiro áudio a Mia avisa o cliente, pausa e passa a conversa para a tela de conversas.',
    resumo: 'chama atendente',
  },
  {
    valor: 'NAO_RESPONDER', icone: 'fa-volume-mute', nome: 'Não responder',
    descricao: 'A Mia ignora o áudio; ele fica registrado na conversa do painel.',
    resumo: 'ignora áudio',
  },
];

export const OPCOES_ENTREGA_CARDAPIO: OpcaoCampoMia[] = [
  {
    valor: 'LINK', icone: 'fa-link', nome: 'Link do cardápio online',
    descricao: 'Mensagem com o botão que abre o cardápio digital.', resumo: 'link',
  },
  {
    valor: 'IMAGEM', icone: 'fa-image', nome: 'Imagem do cardápio',
    descricao: 'A foto do cardápio cadastrada na empresa, como uma pessoa mandaria no WhatsApp.',
    reforco: 'Ideal para quem não usa link.', resumo: 'foto',
  },
  {
    valor: 'IMAGEM_E_LINK', icone: 'fa-images', nome: 'Imagem e link',
    descricao: 'A foto do cardápio e, na sequência, o botão do cardápio online.', resumo: 'foto e link',
  },
];

export const OPCOES_MONTAGEM_PRODUTO: OpcaoCampoMia[] = [
  {
    valor: 'CHAT', icone: 'fa-comment-dots', nome: 'No chat',
    descricao: 'A Mia lista as opções numeradas e o cliente responde por mensagem, um grupo por vez.', resumo: 'no chat',
  },
  {
    valor: 'PAGINA', icone: 'fa-external-link-alt', nome: 'Em uma página',
    descricao: 'A Mia manda um link e o cliente marca tudo de uma vez numa página leve; o item volta para o pedido da conversa.',
    reforco: 'Só para produtos que lista e botões não resolvem.', resumo: 'na página',
  },
];

/** Modelos de IA da Mia V3; vazio = padrão do serviço. Só admin meucardápio altera. */
export const OPCOES_MODELO_IA_V3: OpcaoCampoMia[] = [
  {valor: '', icone: 'fa-microchip', nome: 'Padrão do serviço (GPT-6 Luna)', descricao: '', resumo: 'padrão'},
  {valor: 'claude-sonnet-5', icone: 'fa-microchip', nome: 'Claude Sonnet 5 — $3/$15 MTok', descricao: '', resumo: 'Sonnet 5'},
  {valor: 'claude-haiku-4-5', icone: 'fa-microchip', nome: 'Claude Haiku 4.5 — $1/$5 MTok (econômico)', descricao: '', resumo: 'Haiku 4.5'},
  {valor: 'claude-opus-5', icone: 'fa-microchip', nome: 'Claude Opus 5 — $5/$25 MTok (mais capaz)', descricao: '', resumo: 'Opus 5'},
  {valor: 'gpt-6-luna', icone: 'fa-microchip', nome: 'GPT-6 Luna — $0,10/$0,50 MTok (OpenAI, econômico)', descricao: '', resumo: 'Luna 6'},
  {valor: 'gpt-5.6-terra', icone: 'fa-microchip', nome: 'GPT-5.6 Terra — $2/$12 MTok (OpenAI, balanceado)', descricao: '', resumo: 'Terra'},
  {valor: 'deepseek-v4-flash', icone: 'fa-microchip', nome: 'DeepSeek V4 Flash — $0,14/$0,28 MTok (Responses API, reasoning max)', descricao: '', resumo: 'DeepSeek'},
];

export const CATALOGO_CONFIG_MIA: CampoConfigMia[] = [
  {
    chave: 'status', rotulo: 'Status de operação', tipo: 'escolha', padrao: 'DESATIVADA', icone: 'fa-power-off',
    frase: (v) => ({
      DESATIVADA: 'Está desligada: ninguém recebe resposta automática.',
      MODO_TESTE: 'Está em teste: só os números da lista conversam com ela.',
      ATIVADA: 'Está em produção: atende todos os clientes.',
    } as any)[v] || 'Está desligada: ninguém recebe resposta automática.',
    momento: 'operacao', efeito: 'operacao', quemEdita: 'lojista',
    porQueMudar: 'Comece em Modo Teste com o seu número; passe para Produção quando a Mia responder do jeito que você quer.',
    explicacao: 'Escolha quem recebe e conversa com a Mia no WhatsApp.',
    sinonimos: ['ligar', 'desligar', 'ativar', 'desativar', 'produção', 'teste', 'pausar a mia', 'parar a mia'],
    opcoes: OPCOES_STATUS_MIA,
  },
  {
    chave: 'telefonesTeste', rotulo: 'Telefones liberados para teste', tipo: 'telefones', padrao: '', icone: 'fa-phone',
    frase: (v) => {
      const numeros = String(v || '').split(',').map((t) => t.trim()).filter(Boolean);
      return numeros.length ? `Testa com ${numeros.length === 1 ? 'este número' : `estes ${numeros.length} números`}: ${formateLista(numeros)}.`
        : 'Nenhum número liberado para teste ainda.';
    },
    momento: 'operacao', efeito: 'operacao', quemEdita: 'lojista',
    porQueMudar: 'Coloque o seu número e o da equipe para experimentar a Mia antes de liberar para os clientes.',
    explicacao: 'Digite o número completo com código do país (DDI + DDD + Número). Sem espaços ou traços.',
    sinonimos: ['meu número', 'número de teste', 'testar com meu celular', 'whatsapp de teste'],
    dependeDe: {chave: 'status', valor: 'MODO_TESTE'},
  },
  {
    chave: 'nome', rotulo: 'Nome da atendente', tipo: 'texto', padrao: '', icone: 'fa-user',
    frase: (v) => v ? `Se apresenta como ${v}.` : 'Ainda não tem nome.',
    momento: 'chegada', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Troque se quiser que a atendente se apresente com outro nome.',
    explicacao: 'Nome exibido na saudação inicial (Ex: "Olá! Sou a Mia, atendente do estabelecimento").',
    sinonimos: ['nome da mia', 'como ela se apresenta', 'apelido', 'nome do robô', 'nome da atendente'],
    min: 3,
  },
  {
    chave: 'personalizarSaudacao', rotulo: 'Reconhecer o cliente na saudação', tipo: 'switch', padrao: true, icone: 'fa-user-check',
    frase: (v) => v ? 'Recebe o cliente conhecido pelo nome.'
      : 'Apresenta-se por completo a todo cliente, mesmo a quem já pediu.',
    momento: 'chegada', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Desligue se prefere que todo cliente receba a apresentação completa, como na primeira visita.',
    explicacao: 'Cliente que já pediu é cumprimentado pelo nome, sem a apresentação completa da Mia, como ' +
      'quem reconhece cliente da casa. Desligado, todo mundo recebe a saudação de primeira visita.',
    sinonimos: ['cumprimentar pelo nome', 'saudação', 'cliente da casa', 'oi fulano', 'apresentação', 'cliente conhecido'],
  },
  {
    chave: 'repetirPedido', rotulo: 'Oferecer repetir o último pedido', tipo: 'switch', padrao: true, icone: 'fa-redo',
    frase: (v) => v ? 'Oferece repetir o último pedido a quem já pediu.'
      : 'Não oferece repetir o último pedido; convida a ver o cardápio de hoje.',
    momento: 'chegada', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Desligue se o cardápio muda todo dia (marmitaria com prato do dia): repetir o de ontem não faz sentido.',
    explicacao: 'Ao reconhecer um cliente que já pediu, a Mia oferece repetir o último pedido com os preços ' +
      'de hoje e atende “o de sempre”. Desligue se o cardápio muda todo dia (ex.: marmitaria com prato do dia): ' +
      'a Mia cumprimenta pelo nome e convida a ver o cardápio de hoje.',
    sinonimos: ['o de sempre', 'repetir pedido', 'mesmo de ontem', 'último pedido', 'pedido anterior'],
    dependeDe: {chave: 'carrinhoMiaV3'},
  },

  {
    chave: 'conversaCurta', rotulo: 'Conversa curta', tipo: 'switch', padrao: false, icone: 'fa-comment',
    frase: (v) => v ? 'Conversa curta e direta durante o pedido.'
      : 'Conversa com mais explicações durante o pedido.',
    momento: 'conversa', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Ligue se a Mia está falando demais e o cliente quer só pedir e fechar.',
    explicacao: 'A Mia responde com poucas palavras, mostra os itens e o total em uma linha e ' +
      'pergunta “Algo mais?”. Quando o cliente diz “só isso”, segue para o fechamento sem perguntar ' +
      'de novo. O cliente continua revisando o pedido antes de confirmar.',
    sinonimos: ['fala demais', 'muito texto', 'objetiva', 'direta', 'resposta curta', 'menos conversa', 'prolixa'],
    dependeDe: {chave: 'carrinhoMiaV3'},
  },

  {
    chave: 'comportamentoAudio', rotulo: 'Quando o cliente manda áudio', tipo: 'escolha', padrao: 'PEDIR_TEXTO', icone: 'fa-microphone',
    frase: (v, config) => v === 'CHAMAR_ATENDENTE' && config && config.escalarParaAtendente === false
      ? 'Quando recebe áudio, pede texto porque a transferência para a equipe está desligada.' : ({
      TRANSCREVER: 'Entende os áudios e responde por texto.',
      PEDIR_TEXTO: 'Quando recebe áudio, pede para o cliente escrever.',
      CHAMAR_ATENDENTE: 'Quando recebe áudio, passa a conversa para a equipe.',
      NAO_RESPONDER: 'Ignora áudios; eles ficam registrados no painel.',
    } as any)[v] || 'Quando recebe áudio, pede para o cliente escrever.',
    momento: 'conversa', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Ligue "Entender e responder" se muitos clientes mandam áudio; é o único modo que custa por uso.',
    explicacao: 'Escolha o que a Mia faz quando um áudio chegar. Nos modos de pedir texto ou chamar a equipe, ' +
      'a transferência depende de Ajuda da equipe. Se estiver desligada, pede texto uma vez e fica em silêncio na insistência. ' +
      'Entender e responder e Não responder continuam seguindo suas próprias regras.',
    sinonimos: ['áudio', 'mensagem de voz', 'ouvir', 'transcrever', 'cliente manda áudio', 'voz'],
    opcoes: OPCOES_AUDIO,
  },
  {
    chave: 'entenderImagens', rotulo: 'Entender as imagens do cliente', tipo: 'switch', padrao: true, icone: 'fa-image',
    frase: (v) => v ? 'Entende as fotos e prints que o cliente manda.'
      : 'Não interpreta fotos e prints; eles ficam só para a equipe ver.',
    momento: 'conversa', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Desligue se não quer custo de visão ou se a Mia não deve comentar fotos e comprovantes.',
    explicacao: 'A Mia lê a imagem que o cliente manda (foto de um produto, print, comprovante) e ' +
      'responde sobre ela. Desligado, a imagem continua aparecendo na conversa do painel para a sua equipe, ' +
      'mas a Mia não tenta interpretar o conteúdo.',
    sinonimos: ['foto', 'imagem', 'print', 'comprovante', 'ler foto', 'ver imagem', 'visão'],
  },
  {
    chave: 'instrucoesDaLoja', rotulo: 'Instruções da loja', tipo: 'texto', padrao: '', icone: 'fa-align-left',
    frase: (v) => v ? `Segue instruções específicas da loja (${String(v).length} caracteres).`
      : 'Sem instruções específicas da loja.',
    momento: 'conversa', efeito: 'resposta', quemEdita: 'admin',
    detalhe: true,
    porQueMudar: 'Escreva particularidades que não cabem em toggle: tratamento do nome, tom regional, o que nunca oferecer.',
    explicacao: 'Particularidades desta loja que a Mia deve seguir: tratamento do nome, tom regional, ' +
      'restrições de oferta. Entram no prompt como instruções de estilo — as regras de segurança ' +
      '(nunca inventar itens, preços ou prazos) continuam valendo por cima.',
    sinonimos: ['instruções', 'prompt', 'regra da loja', 'tom', 'jeito de falar', 'nunca oferecer', 'personalidade'],
    maxLength: 1500, unidade: 'caracteres',
  },
  {
    chave: 'divulgarLinkCardapio', rotulo: 'Divulgar o link do cardápio', tipo: 'switch', padrao: false, icone: 'fa-link',
    frase: (v) => v ? 'Manda o link do cardápio já na primeira vez e menciona na saudação.'
      : 'Só manda o link do cardápio quando o cliente pede.',
    momento: 'descoberta', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Ligue se prefere que o cliente peça pelo cardápio digital; desligue se quer que ele peça pela conversa.',
    explicacao: 'A Mia envia o link do cardápio digital já na primeira vez que o cliente quer ver o ' +
      'cardápio, junto das categorias — e a saudação menciona esse caminho. Desligado, o link só vai quando o ' +
      'cliente pede explicitamente.',
    sinonimos: ['link', 'cardápio digital', 'mandar o link', 'site', 'pedir pelo link', 'cardápio online'],
  },
  {
    chave: 'entregaCardapio', rotulo: 'Quando o cliente pede o cardápio', tipo: 'escolha', padrao: 'LINK', icone: 'fa-image',
    frase: (v) => ({
      LINK: 'Envia o cardápio por link.',
      IMAGEM: 'Envia o cardápio em foto.',
      IMAGEM_E_LINK: 'Envia o cardápio em foto e, na sequência, o link.',
    } as any)[v] || 'Envia o cardápio por link.',
    momento: 'descoberta', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Escolha foto se seus clientes estão acostumados a receber a imagem do cardápio no WhatsApp.',
    explicacao: 'A imagem é a mesma do botão “Enviar Imagem do Cardápio”, cadastrada em ' +
      'Cardápio. Sem imagem cadastrada, a Mia envia o link.',
    sinonimos: ['foto do cardápio', 'imagem do cardápio', 'mandar cardápio', 'cardápio em foto', 'link ou foto'],
    opcoes: OPCOES_ENTREGA_CARDAPIO,
  },
  {
    chave: 'imagemCardapioSoDoDia', rotulo: 'Só enviar a imagem se ela foi atualizada hoje', tipo: 'switch', padrao: false, icone: 'fa-calendar-day',
    frase: (v) => v ? 'Só manda a foto do cardápio se ela foi atualizada hoje.'
      : 'Manda a foto do cardápio mesmo que seja de outro dia.',
    momento: 'descoberta', efeito: 'resposta', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Ligue se você troca a foto do cardápio todo dia — a Mia nunca manda a foto de ontem.',
    explicacao: 'Para cardápio do dia (marmitaria, prato feito). Se a imagem não foi trocada hoje, ' +
      'a Mia envia o link no lugar da foto — nunca a foto de ontem.',
    sinonimos: ['cardápio do dia', 'foto de hoje', 'marmita', 'prato do dia', 'foto de ontem', 'atualizada hoje'],
    dependeDe: {chave: 'entregaCardapio', diferenteDe: 'LINK'},
  },
  {
    chave: 'rotuloBotaoCardapio', rotulo: 'Texto do botão do cardápio', tipo: 'texto', padrao: '', icone: 'fa-link',
    frase: (v) => `O botão do cardápio se chama “${v || 'Fazer Pedido'}”.`,
    momento: 'descoberta', efeito: 'resposta', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Troque se "Fazer Pedido" não combina com a sua loja (ex.: "Ver cardápio", "Encomendar").',
    explicacao: 'Texto do botão que abre o cardápio online nas mensagens da Mia. Em branco vale o ' +
      'padrão "Fazer Pedido". Máximo de 20 caracteres (limite do WhatsApp).',
    sinonimos: ['botão', 'texto do botão', 'fazer pedido', 'nome do botão do link'],
    maxLength: 20, unidade: 'caracteres',
  },
  {
    chave: 'rodapeBotaoCardapio', rotulo: 'Frase de apoio do botão', tipo: 'texto', padrao: '', icone: 'fa-link',
    frase: (v) => `Abaixo da mensagem, escreve “${v || 'Toque no botão para fazer seu pedido'}”.`,
    momento: 'descoberta', efeito: 'resposta', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Troque se quer outra frase entre a mensagem e o botão do cardápio.',
    explicacao: 'Frase pequena que aparece entre o texto da mensagem e o botão. Em branco vale o ' +
      'padrão "Toque no botão para fazer seu pedido". Máximo de 60 caracteres.',
    sinonimos: ['rodapé', 'frase do botão', 'toque no botão', 'texto embaixo da mensagem'],
    maxLength: 60, unidade: 'caracteres',
  },

  {
    chave: 'montagemProduto', rotulo: 'Produto com várias escolhas', tipo: 'escolha', padrao: 'CHAT', icone: 'fa-sliders-h',
    frase: (v) => v === 'PAGINA'
      ? 'Manda um link para o cliente montar produtos com várias escolhas numa página.'
      : 'Monta produtos com várias escolhas no próprio chat, um grupo por vez.',
    momento: 'montagem', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Escolha "Em uma página" se seus produtos têm muitos grupos (duas proteínas, três acompanhamentos) e o chat fica longo.',
    explicacao: 'Vale para produtos em que um grupo aceita mais de uma opção (duas proteínas, ' +
      'três acompanhamentos) ou com muitos grupos. Os demais continuam no chat, com lista e botões.',
    dependeDe: {chave: 'carrinhoMiaV3'},
    sinonimos: ['montar produto', 'quentinha', 'marmita com escolhas', 'acompanhamentos', 'página de montagem', 'muitas opções'],
    opcoes: OPCOES_MONTAGEM_PRODUTO,
  },
  {
    chave: 'upsellFechamento', rotulo: 'Upsell: oferecer um item a mais antes de fechar', tipo: 'switch', padrao: false, icone: 'fa-plus-circle',
    frase: (v) => v ? 'Faz upsell: oferece um item a mais antes de fechar, uma vez só.'
      : 'Não faz upsell: fecha o pedido sem oferecer nada a mais.',
    momento: 'montagem', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Upsell é oferecer algo a mais (sobremesa, bebida) na hora de fechar. Ligue para aumentar o ticket: uma oferta só, sem insistir.',
    explicacao: 'Antes de perguntar entrega ou retirada, a Mia sugere um item que combina com o pedido. ' +
      'Se o cliente aceitar, o item entra direto no carrinho com o preço do cardápio.',
    sinonimos: ['upsell', 'oferecer sobremesa', 'oferecer bebida', 'item a mais', 'aumentar ticket', 'sugerir complemento', 'vender mais'],
    dependeDe: {chave: 'carrinhoMiaV3'},
  },
  {
    chave: 'upsellItens', rotulo: 'Itens que a Mia vai oferecer no upsell', tipo: 'produtos', padrao: '', icone: 'fa-list-ul',
    frase: (v) => {
      const n = conteItens(v);
      return n ? `Oferece um dos ${n === 1 ? 'item escolhido' : `${n} itens escolhidos`} por você.`
        : 'Escolhe sozinha o item a oferecer, pelo que os clientes mais pedem junto.';
    },
    momento: 'montagem', efeito: 'resposta', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Escolha até 3 itens se quer mandar o que ela oferece; vazio, ela decide pelo que seus clientes mais pedem junto.',
    explicacao: 'Escolha até 3 itens, na ordem em que a Mia deve preferir. Sem itens escolhidos, ' +
      'ela decide sozinha: sugere o que os seus clientes mais pedem junto, com base nos pedidos dos últimos meses.',
    sinonimos: ['quais itens oferecer', 'produto do upsell', 'sugestão de item', 'o que ela sugere'],
    max: 3, unidade: 'itens',
    dependeDe: {chave: 'upsellFechamento'},
  },

  {
    chave: 'pedirLocalizacao', rotulo: 'Pedir a localização no delivery', tipo: 'switch', padrao: true, icone: 'fa-map-marker-alt',
    frase: (v) => v ? 'Pede a localização 📍 para montar o endereço de entrega.'
      : 'Pede o endereço digitado (rua, número e bairro) na entrega.',
    momento: 'fechamento', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Desligue se seus clientes se confundem com o pin e preferem digitar rua, número e bairro.',
    explicacao: 'No fechamento com entrega, a Mia pede a localização 📍 do cliente para montar o ' +
      'endereço. Desligado, ela pede o endereço digitado (rua, número e bairro). Se o cliente mandar a ' +
      'localização por conta própria, ela continua sendo aproveitada.',
    dependeDe: {chave: 'carrinhoMiaV3'},
    sinonimos: ['localização', 'pin', 'gps', 'endereço', 'digitar endereço', 'mapa', 'entrega'],
  },
  {
    chave: 'sugerirDadosHabituais', rotulo: 'Sugerir os dados de sempre no fechamento', tipo: 'switch', padrao: true, icone: 'fa-magic',
    frase: (v) => v ? 'Sugere o endereço e o pagamento de sempre no fechamento.'
      : 'Pede endereço, entrega e pagamento do zero a cada pedido.',
    momento: 'fechamento', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Desligue se prefere que o cliente escolha endereço, entrega e pagamento do zero a cada pedido.',
    explicacao: 'No fechamento, o endereço de sempre aparece em primeiro e marcado, o modo (entrega ou ' +
      'retirada) e a forma de pagamento habituais vêm como primeira opção. Nada é escolhido sem o toque do cliente, ' +
      'e o troco é perguntado toda vez.',
    sinonimos: ['endereço de sempre', 'pagamento habitual', 'dados salvos', 'lembrar endereço', 'de sempre', 'hábitos'],
    dependeDe: {chave: 'carrinhoMiaV3'},
  },
  {
    chave: 'escalarParaAtendente', rotulo: 'Deixar a Mia chamar uma pessoa da equipe', tipo: 'switch', padrao: true, icone: 'fa-headset',
    frase: (v) => v ? 'Chama sua equipe quando precisa.'
      : 'Nunca chama uma pessoa da equipe: resolve sozinha ou pede desculpas.',
    momento: 'equipe', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Desligue se não há ninguém acompanhando o painel — assim a Mia nunca promete um atendente que não vem.',
    explicacao: 'Quando o cliente pede para falar com um atendente, reclama de um pedido ou a Mia trava, a conversa vai ' +
      'para a tela de conversas e alguém do time assume. Desligue se não há ninguém acompanhando o painel — assim a Mia ' +
      'nunca promete um atendente que não vem.',
    sinonimos: ['atendente', 'falar com humano', 'passar para a equipe', 'transferir', 'escalar', 'chamar alguém', 'ninguém olha o painel'],
  },
  {
    chave: 'mensagemEscalacao', rotulo: 'Aviso ao cliente ao chamar a equipe', tipo: 'texto', padrao: '', icone: 'fa-comment',
    frase: (v) => v ? `Ao passar para a equipe, avisa: “${v}”.`
      : 'Ao passar para a equipe, avisa o cliente com as próprias palavras.',
    momento: 'equipe', efeito: 'resposta', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Preencha se quer controlar exatamente o que o cliente lê quando a conversa passa para a equipe.',
    explicacao: 'O que o cliente recebe no momento em que a conversa é passada para a equipe. Deixe em branco ' +
      'para a Mia avisar com as palavras dela. Evite prometer prazo: ninguém sabe quando alguém vai assumir.',
    sinonimos: ['mensagem de transferência', 'aviso do atendente', 'texto quando chama a equipe', 'já passei sua mensagem'],
    maxLength: 500, unidade: 'caracteres',
    dependeDe: {chave: 'escalarParaAtendente'},
  },
  {
    chave: 'tempoPausaMia', rotulo: 'Tempo de pausa', tipo: 'numero', padrao: 1800, icone: 'fa-clock',
    frase: (v) => `Quando a pausa é temporária, volta após ${formateDuracao(v)}.`,
    momento: 'equipe', efeito: 'operacao', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Ajuste o prazo de retorno após atendimento humano, inclusive quando a equipe assume por conta própria.',
    explicacao: 'Prazo para voltar após uma pausa temporária do atendimento humano. Carrinho em atendimento continua com a equipe até ser liberado.',
    sinonimos: ['silêncio', 'quanto tempo ela fica quieta', 'pausa', 'voltar a responder', 'depois do atendente'],
    unidade: 'segundos', min: 1,
  },

  {
    chave: 'followupMiaV3', rotulo: 'Enviar lembretes proativos', tipo: 'switch', padrao: false, icone: 'fa-bell',
    frase: (v) => v ? 'Manda lembretes a quem some no meio da compra.'
      : 'Não manda lembrete a quem some no meio da compra.',
    momento: 'depois', efeito: 'agenda', quemEdita: 'lojista',
    porQueMudar: 'Ligue para recuperar quem sumiu no meio da compra; desligue se os clientes reclamam de insistência.',
    explicacao: 'A mensagem só sai com a Mia ativa, a loja recebendo pedidos e nenhuma resposta ou pedido novo.',
    sinonimos: ['lembrete', 'follow-up', 'cliente sumiu', 'insistir', 'cutucar', 'retomar conversa', 'enche o saco', 'mensagem depois'],
    dependeDe: {chave: 'carrinhoMiaV3'},
  },
  {
    chave: 'toquesCarrinhoMinutos', rotulo: 'Toques do pedido parado', tipo: 'escada', padrao: TOQUES_PADRAO_CARRINHO, icone: 'fa-clock',
    frase: (v) => `Quem deixou o pedido parado recebe lembrete aos ${formateLista(v) || formateLista(TOQUES_PADRAO_CARRINHO)} minutos.`,
    momento: 'depois', efeito: 'agenda', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Aumente os tempos se os lembretes chegam rápido demais; tire toques se três é muito.',
    explicacao: 'Cliente que montou o pedido e sumiu recebe até 3 lembretes, contados de quando ele ' +
      'parou de responder: o resumo com botões, um empurrãozinho com botão de fechar e um último aviso que ' +
      'encerra de leve.',
    sinonimos: ['pedido parado', 'carrinho parado', 'tempo do lembrete', 'quantos lembretes', 'minutos do toque'],
    unidade: 'minutos', min: 1, max: 60,
    dependeDe: {chave: 'followupMiaV3'},
  },
  {
    chave: 'toquesDescobertaMinutos', rotulo: 'Toques da conversa', tipo: 'escada', padrao: TOQUES_PADRAO_DESCOBERTA, icone: 'fa-comment',
    frase: (v) => `Quem só conversou recebe lembrete aos ${formateLista(v) || formateLista(TOQUES_PADRAO_DESCOBERTA)} minutos.`,
    momento: 'depois', efeito: 'agenda', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Ajuste se quem só perguntou e sumiu deve ser lembrado mais cedo ou mais tarde.',
    explicacao: 'Cliente que só conversou, sem itens no carrinho, recebe toques mais leves — e menos insistentes.',
    sinonimos: ['só conversou', 'sem carrinho', 'perguntou e sumiu', 'lembrete da conversa'],
    unidade: 'minutos', min: 1, max: 60,
    dependeDe: {chave: 'followupMiaV3'},
  },
  {
    chave: 'lembreteCarrinhoMinutos', rotulo: 'Lembrete do carrinho (legado)', tipo: 'numero', padrao: 10, icone: 'fa-clock',
    frase: (v) => `Lembrete único do carrinho aos ${v} minutos (legado).`,
    momento: 'legado', efeito: 'agenda', quemEdita: 'lojista',
    porQueMudar: 'Sem controle na tela: o servidor espelha aqui o 1º toque de "Toques do pedido parado".',
    explicacao: 'Tempo único do lembrete de carrinho, anterior à escada de toques. Mantido para compatibilidade.',
    sinonimos: [],
    unidade: 'minutos', min: 1, max: 60, oculto: true,
  },
  {
    chave: 'lembreteDescobertaMinutos', rotulo: 'Lembrete da conversa (legado)', tipo: 'numero', padrao: 20, icone: 'fa-comment',
    frase: (v) => `Lembrete único da conversa aos ${v} minutos (legado).`,
    momento: 'legado', efeito: 'agenda', quemEdita: 'lojista',
    porQueMudar: 'Sem controle na tela: o servidor espelha aqui o 1º toque de "Toques da conversa".',
    explicacao: 'Tempo único do lembrete de conversa, anterior à escada de toques. Mantido para compatibilidade.',
    sinonimos: [],
    unidade: 'minutos', min: 1, max: 60, oculto: true,
  },
  {
    chave: 'responderSobreProdutos', rotulo: 'Responder sobre produtos do cardápio', tipo: 'switch', padrao: false, icone: 'fa-utensils',
    frase: (v) => v ? 'Responde sobre itens, preços e disponibilidade do cardápio.'
      : 'Não fala de itens e preços; direciona ao cardápio.',
    momento: 'legado', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Ligue para a Mia falar de itens, preços e disponibilidade em vez de só mandar o link.',
    explicacao: 'A Mia usa o cardápio para falar de itens, preços e disponibilidade.',
    sinonimos: ['preço', 'tem tal produto', 'falar do cardápio', 'responder sobre itens', 'disponibilidade'],
    foraDaMiaV3: true,
  },
  {
    // nasce sem escolha: nem a tela nem o domínio têm um valor inicial
    chave: 'comportamentoForaDoEscopo', rotulo: 'Perguntas fora do escopo', tipo: 'escolha', padrao: null, icone: 'fa-question-circle',
    frase: (v) => ({
      RESPONDER: 'Responde mesmo quando a pergunta não é do atendimento.',
      NAO_RESPONDER: 'Ignora perguntas que não são do atendimento.',
      CHAMAR_ATENDENTE: 'Passa para a equipe as perguntas que não são do atendimento.',
    } as any)[v] || 'Ainda não decidiu o que fazer com perguntas fora do atendimento.',
    momento: 'legado', efeito: 'resposta', quemEdita: 'lojista',
    porQueMudar: 'Escolha o que acontece quando perguntam algo que não é do atendimento (clima, futebol, outra loja).',
    explicacao: 'O que a Mia faz quando perguntam algo que não é do atendimento.',
    sinonimos: ['assunto aleatório', 'pergunta que não é do pedido', 'fora do assunto', 'conversa fiada', 'não é sobre a loja'],
    opcoes: OPCOES_FORA_DO_ESCOPO,
    foraDaMiaV3: true,
  },
  {
    chave: 'recuperarCarrinho', rotulo: 'Lembrar cliente do carrinho abandonado', tipo: 'switch', padrao: false, icone: 'fa-bell',
    frase: (v) => v ? 'Lembra quem abandonou o carrinho no cardápio digital.'
      : 'Não lembra quem abandonou o carrinho no cardápio digital.',
    momento: 'legado', efeito: 'agenda', quemEdita: 'lojista',
    porQueMudar: 'Ligue para mandar uma mensagem a quem montou o pedido no cardápio digital e não finalizou.',
    explicacao: 'A Mia manda uma mensagem depois do tempo que você definir, para trazer o cliente de volta.',
    sinonimos: ['carrinho abandonado', 'não finalizou', 'recuperar carrinho', 'abandonou o pedido'],
    foraDaMiaV3: true,
  },
  {
    chave: 'tempoRecuperarCarrinho', rotulo: 'Esperar antes de enviar', tipo: 'numero', padrao: 1200, icone: 'fa-hourglass-half',
    frase: (v) => `Espera ${formateDuracao(v)} antes de lembrar do carrinho abandonado.`,
    momento: 'legado', efeito: 'agenda', quemEdita: 'lojista',
    detalhe: true,
    porQueMudar: 'Diminua se o cliente some rápido; aumente se a mensagem está chegando enquanto ele ainda decide.',
    explicacao: 'Tempo suficiente para o cliente decidir, curto o bastante para não perder a oportunidade.',
    sinonimos: ['tempo do carrinho abandonado', 'esperar quanto', 'depois de quanto tempo'],
    unidade: 'segundos', min: 1,
    dependeDe: {chave: 'recuperarCarrinho'},
    foraDaMiaV3: true,
  },

  {
    chave: 'usarFluxoTypebot', rotulo: 'Usar fluxo de conversas', tipo: 'switch', padrao: false, icone: 'fa-project-diagram',
    frase: (v) => v ? 'Abre a conversa com um menu guiado.' : 'Não abre a conversa com menu guiado.',
    momento: 'legado', efeito: 'motor', quemEdita: 'admin',
    porQueMudar: 'Configure o menu do bot antigo na seção de configurações antigas da tela avançada.',
    explicacao: 'O cliente escolhe o que quer em um menu e a Mia continua a partir dali.',
    sinonimos: ['menu', 'typebot', 'fluxo', 'menu guiado', 'opções no início'],
    somenteAvancado: true, foraDaMiaV3: true,
  },
  {
    chave: 'modelo', rotulo: 'Plano da Mia (legado)', tipo: 'texto', padrao: 'Basico', icone: 'fa-microchip',
    frase: (v) => `Plano ${v || 'Basico'} do bot antigo.`,
    momento: 'legado', efeito: 'motor', quemEdita: 'admin',
    porQueMudar: 'Sem controle na tela: plano do bot antigo (Basico/Intermediario/Avancado), anterior à Mia V3.',
    explicacao: 'Plano contratado do bot antigo. A Mia V3 usa "Modelo de IA".',
    sinonimos: [],
    oculto: true,
  },  {
    chave: 'usarMiaV3', rotulo: 'Atender pela Mia V3', tipo: 'switch', padrao: false, icone: 'fa-robot',
    frase: (v) => v ? 'Atende pela Mia V3 no WhatsApp oficial.' : 'Ainda atende pelo bot antigo.',
    momento: 'motor', efeito: 'motor', quemEdita: 'admin',
    porQueMudar: 'Liga a vendedora conversacional no WhatsApp oficial no lugar do bot antigo.',
    explicacao: 'Substitui o bot antigo no WhatsApp oficial.',
    sinonimos: ['mia v3', 'motor novo', 'vendedora', 'bot antigo'],
  },
  {
    chave: 'carrinhoMiaV3', rotulo: 'Carrinho conversacional', tipo: 'switch', padrao: false, icone: 'fa-shopping-cart',
    frase: (v) => v ? 'Monta e fecha o pedido no chat.'
      : 'Só tira dúvidas e direciona ao cardápio; não monta pedido no chat.',
    momento: 'motor', efeito: 'motor', quemEdita: 'admin',
    porQueMudar: 'Sem ele a Mia V3 só tira dúvidas e direciona ao cardápio; com ele, monta e fecha o pedido no chat.',
    explicacao: 'Monta o pedido e fecha a venda no chat. Sem ele, a Mia V3 só tira dúvidas e direciona ao cardápio.',
    sinonimos: ['carrinho', 'pedir pelo chat', 'fechar pedido na conversa', 'vender no whatsapp'],
    dependeDe: {chave: 'usarMiaV3'},
  },
  {
    chave: 'modeloIaV3', rotulo: 'Modelo de IA', tipo: 'escolha', padrao: '', icone: 'fa-microchip',
    frase: (v) => `Pensa com ${nomeDaOpcao(OPCOES_MODELO_IA_V3, v || '')}.`,
    momento: 'motor', efeito: 'motor', quemEdita: 'admin',
    porQueMudar: 'Troque para comparar custo e qualidade entre modelos; vazio usa o padrão do serviço.',
    explicacao: 'Motor inteligente responsável por interpretar dúvidas do cardápio e sugestões.',
    sinonimos: ['modelo', 'llm', 'gpt', 'claude', 'custo por token', 'inteligência'],
    opcoes: OPCOES_MODELO_IA_V3,
  },
];

export function campoDoCatalogo(chave: string): CampoConfigMia | undefined {
  return CATALOGO_CONFIG_MIA.find((campo) => campo.chave === chave);
}

const OPCOES_AUDIO_SEM_EQUIPE = OPCOES_AUDIO.map((opcao) => {
  if (opcao.valor === 'CHAMAR_ATENDENTE') return {...opcao,
    descricao: 'A transferência está desligada em Ajuda da equipe. A Mia pede a mensagem por texto.',
    reforco: 'Se o cliente insistir, fica em silêncio.'};
  if (opcao.valor === 'PEDIR_TEXTO') return {...opcao,
    reforco: 'Se o cliente insistir, fica em silêncio porque a transferência para a equipe está desligada.'};
  return opcao;
});

export function opcoesDoCampo(chave: string, config?: any): OpcaoCampoMia[] {
  if (chave === 'comportamentoAudio' && config && config.escalarParaAtendente === false) return OPCOES_AUDIO_SEM_EQUIPE;
  return campoDoCatalogo(chave)?.opcoes || [];
}

/** Grupos de um bloco do trilho, na ordem do catálogo. */
export function gruposDoBloco(bloco: BlocoDeGrupos) {
  return GRUPOS_CONFIG_MIA.filter((grupo) => grupo.bloco === bloco);
}

export function camposDoMomento(momento: string): CampoConfigMia[] {
  return CATALOGO_CONFIG_MIA.filter((campo) => campo.momento === momento && !campo.oculto);
}

/** Devolve a primeira dependência não atendida, inclusive em cadeias. */
export function dependenciaPendente(chave: string, config: any): DependenciaDeCampo | undefined {
  const dependencia = campoDoCatalogo(chave)?.dependeDe;
  if (!dependencia) return undefined;
  const anterior = dependenciaPendente(dependencia.chave, config);
  if (anterior) return anterior;
  const valor = config && config[dependencia.chave];
  const satisfeita = dependencia.diferenteDe !== undefined ? valor !== dependencia.diferenteDe
    : dependencia.valor !== undefined ? valor === dependencia.valor : !!valor;
  return satisfeita ? undefined : dependencia;
}

export function campoDisponivel(chave: string, config: any): boolean {
  return !dependenciaPendente(chave, config);
}

export function bloqueioDoCampo(chave: string, config: any, ehAdmin: boolean): string {
  const dependencia = dependenciaPendente(chave, config);
  if (!dependencia) return '';
  const pai = campoDoCatalogo(dependencia.chave)!;
  if (pai.quemEdita === 'admin' && !ehAdmin) {
    return 'Disponível quando o administrador habilitar “' + pai.rotulo + '” para a loja.';
  }
  const opcao = (pai.opcoes || []).find((item) => item.valor === dependencia.valor);
  if (opcao) return 'Disponível quando “' + pai.rotulo + '” estiver em “' + opcao.nome + '”.';
  if (dependencia.diferenteDe !== undefined) {
    const atual = (pai.opcoes || []).find((item) => item.valor === dependencia.diferenteDe);
    return 'Disponível ao escolher outra opção em “' + pai.rotulo + '”, além de “' + (atual ? atual.nome : dependencia.diferenteDe) + '”.';
  }
  return 'Disponível ao ativar “' + pai.rotulo + '”.';
}

/** Campos que o lojista vê e mexe na tela — sem os ocultos e sem os de admin. */
export function camposDoLojista(): CampoConfigMia[] {
  return CATALOGO_CONFIG_MIA.filter((campo) => !campo.oculto && campo.quemEdita === 'lojista');
}
